import Store from '@store';
import { AdvancedOrderedListChainHandler } from './chain-handler';
import * as TextStylingService from 'src/services/text-styling';
import { logError } from 'src/utils/logger';
import { constants, text } from '@config';
import { getTextMockInstance } from 'src/factories/textmock';
import { splitLinkedTextAreaLink } from 'src/helpers/text-overflow-links';
import { getLargestNumberFromObjectKey } from 'src/utils/format';

export class AdvancedOrderedListFactory {
  #editor;
  #canvas;
  #chunk;
  #change;
  #chainHandler;
  #orderTracking;
  #orderedData;
  #parser;

  #store;
  #textMock;

  constructor () {
    this.#store = Store;
    this.#editor = INKIVE.Tool.getInstance().editor;
    this.#canvas = this.#editor.canvas;
    this.#change = false;
    this.#chainHandler = new AdvancedOrderedListChainHandler();
    this.#orderTracking = {};
    this.#textMock = getTextMockInstance();
    this.#parser = new DOMParser();
    this.#orderedData = {
      style: [],
      startFrom: [],
      level: [],
      numberingStyle: [],
      regex: []
    };
  }

  get orderTracking () {
    return JSON.parse(JSON.stringify(this.#orderTracking));
  }

  get orderedData () {
    return JSON.parse(JSON.stringify(this.#orderedData));
  }

  #getOrderedListMetadata () {
    const state = this.#store.getState();
    const chains = state.metadata?.bullet_and_ordered_list?.list_styles;
    if (!chains) return null;

    return chains ?? null;
  }

  #formatChain (chain) {
    return chain.map(item => ({
      pageId: Number(item.page_id),
      id: item.element_id
    }));
  }

  #isElementInChain (chain, { pageId, elementId }) {
    // TODO: Remove number transformation after fixing the API
    return chain.some(item => item.element_id === elementId && Number(item.page_id) === pageId);
  }

  calculateOrderTrackingFromPreviousPages (pageId = INKIVE.Tool._current_design_id) {
    const chains = this.#chainHandler.extractChainsFromPreviousPages(pageId);
    const chainsArray = this.#chainHandler.convertChainsToArray(chains);
    const textsLinkId = this.#chainHandler.sortTextsToRender(chainsArray);

    if (!chains) return;

    this.#orderedData = {
      style: [],
      startFrom: [],
      level: [],
      numberingStyle: [],
      regex: []
    };

    for (const textLink of textsLinkId) {
      const [pageId, id] = splitLinkedTextAreaLink(textLink);
      const elementData = this.#chainHandler.getElementOrderedDataFromStore(pageId, id);
      this.#orderedData.style = this.#orderedData.style.concat(elementData.style);
      this.#orderedData.level = this.#orderedData.level.concat(elementData.level);
      this.#orderedData.startFrom = this.#orderedData.startFrom.concat(elementData.startFrom);
      this.#orderedData.numberingStyle = this.#orderedData.numberingStyle.concat(elementData.numberingStyle);
      this.#orderedData.regex = this.#orderedData.regex.concat(elementData.regex);
    }

    this.#orderTracking = TextStylingService.calculateOrderTracking(this.#orderedData.level, this.#orderedData.startFrom, this.#orderedData.style);
    return this.#orderTracking;
  }

  updateTextsInPage (pageId = INKIVE.Tool._current_design_id) {
    const chains = this.#chainHandler.extractChainOnPage(pageId);
    const chainsArray = this.#chainHandler.convertChainsToArray(chains);
    const textsToRender = this.#chainHandler.sortTextsToRender(chainsArray);
    const orderedData = {
      dom: [],
      style: [],
      startFrom: [],
      level: [],
      numberingStyle: [],
      regex: []
    };

    for (const textLink of textsToRender) {
      const [pageId, id] = splitLinkedTextAreaLink(textLink);
      const orderedAttributesArray = this.#calculateTextOrderedAttributesArray(Number(pageId), id);

      orderedData.dom = orderedData.dom.concat(orderedAttributesArray.dom);
      orderedData.style = orderedData.style.concat(orderedAttributesArray.style);
      orderedData.startFrom = orderedData.startFrom.concat(orderedAttributesArray.startFrom);
      orderedData.level = orderedData.level.concat(orderedAttributesArray.level);
      orderedData.numberingStyle = orderedData.numberingStyle.concat(orderedAttributesArray.numberingStyle);
      orderedData.regex = orderedData.regex.concat(orderedAttributesArray.regex);
    }

    const orderTracking = JSON.parse(JSON.stringify(this.#orderTracking));
    const orderArray = TextStylingService.calculateAdvanceOrderContent(
      orderedData.level,
      orderedData.startFrom,
      orderedData.style,
      orderTracking,
      null,
      orderedData.numberingStyle,
      orderedData.regex
    );
    this.#updateOrderedListDOM(orderedData.dom, orderArray);
  }

  updateTextsInChain (pageId = INKIVE.Tool._current_design_id, elementId, chains) {
    chains = chains || this.#chainHandler.extractChainsAfterElement(pageId, elementId);

    this.#chainHandler.filterChainByPage(chains, pageId);

    const prevOrderedData = this.#orderedData;
    for (const styleId in chains) {
      const chain = chains[styleId];
      if (!chain.length) continue;

      for (const element of chain) {
        const currentPageOrderedData = this.#chainHandler.getElementOrderedDataOnCurrentPage(element.id);
        const orderedData = {
          style: prevOrderedData.style.concat(currentPageOrderedData.style),
          startFrom: prevOrderedData.startFrom.concat(currentPageOrderedData.startFrom),
          level: prevOrderedData.level.concat(currentPageOrderedData.level),
          numberingStyle: prevOrderedData.numberingStyle.concat(currentPageOrderedData.numberingStyle),
          regex: prevOrderedData.regex.concat(currentPageOrderedData.regex)
        };
        const advancedOrderedListTracking = TextStylingService.calculateOrderTracking(orderedData.level, orderedData.startFrom, orderedData.style);
        const styleTracking = { [styleId]: { ...(advancedOrderedListTracking[styleId]) } };
        const orderedAttributesArray = this.#calculateTextOrderedAttributesArray(Number(element.pageId), element.id, styleId);
        const orderArray = TextStylingService.calculateAdvanceOrderContent(
          orderedAttributesArray.level,
          orderedAttributesArray.startFrom,
          orderedAttributesArray.style,
          styleTracking,
          null,
          orderedAttributesArray.numberingStyle,
          orderedAttributesArray.regex
        );
        this.#updateOrderedListDOM(orderedAttributesArray.dom, orderArray);
      }
    }
  }

  renderHiddenTexts () {
    const chains = this.#chainHandler.extractChainsFromNextPages(INKIVE.Tool._current_design_id);
    const chainsArray = this.#chainHandler.convertChainsToArray(chains);
    const textsToRender = this.#chainHandler.sortTextsToRender(chainsArray);

    const textMockNodeString = this.calculateTextMockNodeString(textsToRender);
    const textMockFragment = this.#parser.parseFromString(textMockNodeString, 'text/html').body.childNodes[0];

    const prevOrderedData = this.#orderedData;
    const currentPageOrderedData = this.#chainHandler.getElementsOrderedDataOnCurrentPage();
    const orderedData = {
      style: prevOrderedData.style.concat(currentPageOrderedData.style),
      startFrom: prevOrderedData.startFrom.concat(currentPageOrderedData.startFrom),
      level: prevOrderedData.level.concat(currentPageOrderedData.level),
      numberingStyle: prevOrderedData.numberingStyle.concat(currentPageOrderedData.numberingStyle),
      regex: prevOrderedData.regex.concat(currentPageOrderedData.regex)
    };

    const styleTracking = TextStylingService.calculateOrderTracking(orderedData.level, orderedData.startFrom, orderedData.style);
    const orderedAttributesArray = this.#calculateAttributesArrayFromFragment(textsToRender, textMockFragment, chains);
    const orderArray = TextStylingService.calculateAdvanceOrderContent(
      orderedAttributesArray.level,
      orderedAttributesArray.startFrom,
      orderedAttributesArray.style,
      styleTracking,
      null,
      orderedAttributesArray.numberingStyle,
      orderedAttributesArray.regex
    );

    const changedDOM = this.#updateOrderedListDOM(
      orderedAttributesArray.dom,
      orderArray
    );
    const changedTextarea = changedDOM.map(element => element.closest('.textarea'));

    return changedTextarea;
  }

  calculateTextMockNodeString (textsToRender) {
    const openSvgTag = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">';
    const closeSvgTag = '</svg>';
    let content = '';

    for (const text of textsToRender) {
      const [pageId, id] = splitLinkedTextAreaLink(text);
      const textNodeString = this.#chainHandler.getElementNodeStringFromStore(pageId, id);

      content += textNodeString;
    }

    return openSvgTag + content + closeSvgTag;
  }

  #updateOrderedListDOM (domArray, orderArray) {
    const changedDOM = [];
    for (let i = 0; i < domArray.length; i++) {
      const text = domArray[i];
      const tspan = text?.querySelector('tspan.ordered');
      const orderContent = orderArray[i];

      if (!tspan || !orderContent) continue;
      const newContent = orderContent + constants.text.noBreakSpace;
      if (this.#sanitizeSpace(tspan.textContent) === this.#sanitizeSpace(newContent)) continue;

      const dx = text.getAttribute('dx');
      if (newContent.length > tspan.textContent.length && dx) {
        text.setAttribute('dx', '0 '.repeat(newContent.length - tspan.textContent.length) + dx);
      } else if (newContent.length < tspan.textContent.length && dx) {
        text.setAttribute('dx', '0 '.repeat(newContent.length) + dx.split(' ').slice(tspan.textContent.length).join(' '));
      }

      tspan.textContent = newContent;
      changedDOM.push(text);
    }

    return changedDOM;
  }

  #sanitizeSpace (string) {
    // Replace all spaces with non-breaking spaces.
    return string.replace(/ /g, constants.text.noBreakSpace);
  }

  #calculateAttributesArrayFromFragment (textsToRender, domFragment, chains) {
    const orderedArrayData = {
      dom: [],
      style: [],
      level: [],
      startFrom: [],
      numberingStyle: [],
      regex: []
    };
    const textMock = document.querySelector('#textmock');
    for (const linkId of textsToRender) {
      const [pageId, id] = splitLinkedTextAreaLink(linkId);
      const textarea = textMock?.querySelector(`g#${id}[data-page-id="${pageId}"]`) || domFragment.querySelector(`g#${id}[data-page-id="${pageId}"]`);

      if (!textarea) continue;
      const headTexts = [...textarea.querySelectorAll('text:first-child, text.eol + text.eol, text.eol + text:not(.eol)')];

      for (const text of headTexts) {
        if (!chains[text.dataset.orderedStyle]?.length) continue;

        const style = Number(text.dataset.orderedStyle) || 0;
        const level = Number(text.dataset.ordered) || 0;
        const startFrom = Number(text.dataset.orderedStart) || 0;
        const numberingStyle = Number(text.dataset.orderedNumberingStyle) || 0;
        const regex = text.dataset.orderedRegex || '';

        orderedArrayData.dom.push(text);
        orderedArrayData.style.push(style);
        orderedArrayData.level.push(level);
        orderedArrayData.startFrom.push(startFrom);
        orderedArrayData.numberingStyle.push(numberingStyle);
        orderedArrayData.regex.push(regex);
      }
    }

    return orderedArrayData;
  }

  #calculateTextOrderedAttributesArray (pageId, elementId, styleId) {
    const orderedArrayData = {
      dom: [],
      style: [],
      level: [],
      startFrom: [],
      numberingStyle: [],
      regex: []
    };

    if (pageId !== INKIVE.Tool._current_design_id) {
      logError('calculateTextOrderedAttributesArray')(new Error('This function must be called for current page'));
      return orderedArrayData;
    }

    const textarea = document.querySelector(`#svgcontent #${elementId}`);
    const headTextsNodeList = textarea?.querySelectorAll('text:first-child, text.eol + text.eol, text.eol + text:not(.eol)');
    const headTexts = headTextsNodeList ? [...headTextsNodeList] : [];

    for (const text of headTexts) {
      if (styleId && Number(text.dataset.orderedStyle) !== Number(styleId)) continue;

      const style = Number(text.dataset.orderedStyle) || 0;
      const level = Number(text.dataset.ordered) || 0;
      const startFrom = Number(text.dataset.orderedStart) || 0;
      const numberingStyle = Number(text.dataset.orderedNumberingStyle) || 0;
      const regex = text.dataset.orderedRegex || '';

      orderedArrayData.dom.push(text);
      orderedArrayData.style.push(style);
      orderedArrayData.level.push(level);
      orderedArrayData.startFrom.push(startFrom);
      orderedArrayData.numberingStyle.push(numberingStyle);
      orderedArrayData.regex.push(regex);
    }

    return orderedArrayData;
  }

  #findBulletKey (style) {
    for (const key in text.orderedList.stylesMapping) {
      if (text.orderedList.stylesMapping[key] === style) {
        return key;
      }
    }
    return 0;
  }

  #getStyleId () {
    const listStylesData = this.#store.getState().text.orderedList.styles;

    return {
      list: getLargestNumberFromObjectKey(listStylesData)
    };
  }

  isElementInOrderedListChain (pageId, elementId) {
    const orderedListMetadata = this.#getOrderedListMetadata();
    if (!orderedListMetadata) return false;

    return Object.values(orderedListMetadata).some(style => {
      if (!style.continue_across_stories) return false;

      return this.#isElementInChain(style.chains, { pageId, elementId });
    });
  }

  /**
  * Find chains that have specific element
  * @function advancedOrderedList:chainHandler.findChainByElementId
  * @param {number} pageId - The query page ID
  * @param {string} elementId - The query element ID
  * @returns {object} chains e.g. chains = {56: [...], 101: [...]}
  */
  findChainByElementId (pageId, elementId) {
    let chains = null;

    const orderedListMetadata = this.#getOrderedListMetadata();
    if (!orderedListMetadata) return chains;

    for (const styleId in orderedListMetadata) {
      const style = orderedListMetadata[styleId];
      if (!style.continue_across_stories) continue;

      if (this.#isElementInChain(style.chains, { pageId, elementId })) {
        chains = chains ?? {};
        chains[styleId] = this.#formatChain(style.chains);
      }
    }

    return chains;
  }

  /**
  * Find chains that have specific page ID
  * @function advancedOrderedList:chainHandler.findChainByPageId
  * @param {number} pageId - The query page ID
  * @returns {object} chains e.g. chains = {56: [...], 101: [...]}
  */
  findChainByPageId (pageId) {
    let chains = null;

    const orderedListMetadata = this.#getOrderedListMetadata();
    if (!orderedListMetadata) return chains;

    for (const styleId in orderedListMetadata) {
      const style = orderedListMetadata[styleId];
      if (!style.continue_across_stories) continue;

      const isPageInTheChain = style.chains.some(item => Number(item.page_id) === pageId);
      if (isPageInTheChain) {
        chains = chains ?? {};
        chains[styleId] = this.#formatChain(style.chains);
      }
    }

    return chains;
  }

  /**
  * Find pages that are in the same chain
  * @function advancedOrderedList:chainHandler.findPagesInSameChain
  * @param {number} pageId - The query page ID
  * @returns {object} pages e.g. pages = [101, 102, ...]
  */
  findPagesInSameChain (pageId) {
    const pages = new Set();

    const orderedListMetadata = this.#getOrderedListMetadata();
    if (!orderedListMetadata) return pages;

    for (const styleId in orderedListMetadata) {
      const style = orderedListMetadata[styleId];
      if (!style.continue_across_stories) continue;

      const isPageInTheChain = style.chains.some(item => Number(item.page_id) === pageId);
      if (isPageInTheChain) {
        style.chains.forEach(item => {
          if (Number(item.page_id) === pageId) return;
          pages.add(Number(item.page_id));
        });
      }
    }

    return pages;
  }

  /**
  * Find pages that contain ordered list chain
  * @function advancedOrderedList:chainHandler.findPagesContainChain
  * @param {number} pageId - The query page ID
  * @returns {object} pages e.g. pages = [101, 102, ...]
  */
  findPagesContainChain (pageId) {
    const pages = new Set();

    const orderedListMetadata = this.#getOrderedListMetadata();
    if (!orderedListMetadata) return pages;

    for (const styleId in orderedListMetadata) {
      const style = orderedListMetadata[styleId];
      if (!style.continue_across_stories) continue;

      style.chains.forEach(item => {
        if (Number(item.page_id) === pageId) return;
        pages.add(Number(item.page_id));
      });
    }

    return pages;
  }

  filterElementsOfCurrentChain (pageId, textElements) {
    // TODO: findChainByPageId is heavy to run for each page, needs to be optimized
    const chains = this.findChainByPageId(pageId);
    if (!chains) return [];

    const allowedElements = Object.values(chains).flat();
    return textElements.filter(({ id, pageId }) => allowedElements.some(item => item.id === id && item.pageId === pageId));
  }

  updateExistingMetadata (selectedOrderedStyle, selectedValues, regexArray) {
    const metadata = this.#store.getState().metadata.bullet_and_ordered_list;
    const listStyleMetadata = metadata.list_styles;
    const listStyle = listStyleMetadata[selectedOrderedStyle];
    const levelStyles = [];
    const updatedListStyle = {};

    for (const [index, value] of selectedValues.entries()) {
      levelStyles.push({
        numbering_style: this.#findBulletKey(value),
        numbering_regex: regexArray[index]
      });
    }

    updatedListStyle[selectedOrderedStyle] = { ...listStyle, level_styles: levelStyles };

    return {
      bullet_and_ordered_list: {
        ...metadata,
        list_styles: { ...listStyleMetadata, ...updatedListStyle }
      }
    };
  }

  createNewMetadata (selectedValues, acrossStories, regexArray) {
    const metadata = this.#store.getState().metadata.bullet_and_ordered_list;
    const levelStyles = [];
    const listStyles = {};
    const { list: listStyleId } = this.#getStyleId();

    for (const [index, value] of selectedValues.entries()) {
      levelStyles.push({
        numbering_style: this.#findBulletKey(value),
        numbering_regex: regexArray[index]
      });
    }

    listStyles[listStyleId + 1] = {
      chains: [],
      continue_across_stories: acrossStories,
      name: `Custom ordered list style ${listStyleId + 1}`,
      level_styles: levelStyles
    };

    return {
      bullet_and_ordered_list: {
        ...metadata,
        list_styles: { ...metadata?.list_styles, ...listStyles }
      }
    };
  }
}
